import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import '@/styles/admin.css';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'partial', label: 'Partial' },
  { key: 'released', label: 'Released' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'test_excluded', label: 'Test / Excluded' },
];
const PW_STORAGE_KEY = 'nymphe_admin_pw';

// ─── Admin auth gate (env-based password, stored in sessionStorage) ────
const AdminGate = ({ onAuthed }) => {
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await axios.get(`${API}/api/waitlist/stats`, {
        headers: { 'X-Admin-Password': pw },
      });
      // /stats isn't gated, so probe list to actually verify the pw
      await axios.get(`${API}/api/waitlist/list?status=pending`, {
        headers: { 'X-Admin-Password': pw },
      });
      sessionStorage.setItem(PW_STORAGE_KEY, pw);
      onAuthed(pw);
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.error('Admin is not configured on the server.');
      } else {
        toast.error('Incorrect password');
      }
    }
    setLoading(false);
  };

  return (
    <div className="adm adm--gate" data-testid="admin-gate">
      <form className="adm-gate-form" onSubmit={submit}>
        <h1 className="adm-gate-title">Admin</h1>
        <input
          type="password"
          className="adm-gate-input"
          placeholder="Password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="admin-gate-input"
          autoFocus
        />
        <button
          type="submit"
          className="adm-gate-submit"
          disabled={loading || !pw}
          data-testid="admin-gate-submit"
        >
          {loading ? '...' : 'Enter'}
        </button>
      </form>
    </div>
  );
};

const AdminPage = () => {
  const [authPw, setAuthPw] = useState(() => sessionStorage.getItem(PW_STORAGE_KEY) || '');
  const [applicants, setApplicants] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState({});      // { [id]: true }
  const [batchCount, setBatchCount] = useState(10);
  const [batchRunning, setBatchRunning] = useState(false);

  // ─── CSV Import state ────────────────────────────────────────
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);   // last dry-run report for currently-selected file
  const [csvWorking, setCsvWorking] = useState(false);
  const [csvImported, setCsvImported] = useState(null); // last real-import report

  // ─── Change-password state ───────────────────────────────────
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwWorking, setPwWorking] = useState(false);

  // ─── Access Funnel (read-only) ───────────────────────────────
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);

  // ─── Promo Code Performance (read-only) ──────────────────────
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(true);
  const [promoDays, setPromoDays] = useState(30);

  // ─── Filter pill counters ────────────────────────────────────
  const [counts, setCounts] = useState(null);

  // ─── Launch Balance Tracker ──────────────────────────────────
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // ─── Wave Release Control ────────────────────────────────────
  const [waveType, setWaveType] = useState('first');
  const [wavePreview, setWavePreview] = useState(null);
  const [waveWorking, setWaveWorking] = useState(false);
  const [waveSelection, setWaveSelection] = useState({}); // { [user_id]: bool }
  const [waveNumber, setWaveNumber] = useState(1);

  // ─── Edit modal ──────────────────────────────────────────────
  const [editing, setEditing] = useState(null); // applicant being edited
  const [editForm, setEditForm] = useState({});
  const [editWorking, setEditWorking] = useState(false);


  const authHeaders = { 'X-Admin-Password': authPw };

  const fetchApplicants = useCallback(async () => {
    if (!authPw) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (search.trim()) params.set('search', search.trim());
      const { data } = await axios.get(`${API}/api/waitlist/list?${params}`, {
        headers: authHeaders,
      });
      setApplicants(data.applicants || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        // Password invalidated (e.g. server rotated). Kick out.
        sessionStorage.removeItem(PW_STORAGE_KEY);
        setAuthPw('');
      } else {
        toast.error('Failed to load applicants');
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search, authPw]);

  useEffect(() => {
    const t = setTimeout(fetchApplicants, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchApplicants, search]);

  // Read-only Access Funnel fetch (once per admin session load)
  const fetchFunnel = useCallback(async () => {
    if (!authPw) return;
    setFunnelLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/funnel/access`, {
        headers: authHeaders,
      });
      setFunnel(data);
    } catch {
      // silent — funnel is additive, never blocks the page
      setFunnel(null);
    }
    setFunnelLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPw]);

  useEffect(() => {
    fetchFunnel();
  }, [fetchFunnel]);

  // Read-only Promo Code Performance fetch — refetches on timeframe toggle
  const fetchPromo = useCallback(async () => {
    if (!authPw) return;
    setPromoLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/promo-codes?days=${promoDays}`,
        { headers: authHeaders },
      );
      setPromo(data);
    } catch {
      setPromo(null);
    }
    setPromoLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPw, promoDays]);

  useEffect(() => {
    fetchPromo();
  }, [fetchPromo]);

  // ─── Filter counts ────────────────────────────────────────────
  const fetchCounts = useCallback(async () => {
    if (!authPw) return;
    try {
      const { data } = await axios.get(`${API}/api/admin/waitlist/counts`, {
        headers: authHeaders,
      });
      setCounts(data);
    } catch {
      setCounts(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPw]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, applicants.length]);

  // ─── Launch Balance Tracker ───────────────────────────────────
  const fetchBalance = useCallback(async () => {
    if (!authPw) return;
    setBalanceLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/launch-balance`, {
        headers: authHeaders,
      });
      setBalance(data);
    } catch {
      setBalance(null);
    }
    setBalanceLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPw]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // ─── Wave preview ─────────────────────────────────────────────
  const handleGenerateWave = async () => {
    if (!authPw) return;
    setWaveWorking(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/wave/preview?wave_type=${waveType}`,
        { headers: authHeaders },
      );
      setWavePreview(data);
      // Default-select all picked applicants for the wave.
      const sel = {};
      (data.applicants || []).forEach((a) => { sel[a.id] = true; });
      setWaveSelection(sel);
    } catch {
      toast.error('Could not generate wave preview.');
    }
    setWaveWorking(false);
  };

  const handleReleaseWave = async () => {
    if (!wavePreview || !authPw) return;
    const ids = (wavePreview.applicants || [])
      .filter((a) => waveSelection[a.id])
      .map((a) => a.id);
    if (!ids.length) {
      toast.error('No applicants selected.');
      return;
    }
    if (!window.confirm(
      `Send ${ids.length} invite email${ids.length === 1 ? '' : 's'} for Wave ${waveNumber}?\n\nThis cannot be undone.`,
    )) {
      return;
    }
    setWaveWorking(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/wave/release`,
        {
          user_ids: ids,
          wave_number: Number(waveNumber) || 1,
          release_reason: waveType === 'first' ? 'wave_1_launch_balance' : 'suggested_wave_release',
          release_mode: 'ai_suggested',
        },
        { headers: authHeaders },
      );
      // Top-level message reflects real outcome (released vs failed +
      // first reason). Show the right toast variant accordingly.
      if (data.released_count > 0 && data.failed_count === 0) {
        toast.success(data.message);
      } else if (data.failed_count > 0) {
        toast.error(data.message);
      } else {
        toast.message(data.message);
      }
      if (data.skipped_count > 0) {
        toast.message(`${data.skipped_count} skipped (test / already released).`);
      }
      setWavePreview(null);
      setWaveSelection({});
      fetchApplicants();
      fetchCounts();
      fetchBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Wave release failed.');
    }
    setWaveWorking(false);
  };

  const handleExportWaveCsv = () => {
    if (!wavePreview) return;
    const rows = [
      ['email', 'name', 'identities', 'source', 'referral_code', 'created_at', 'category'],
      ...(wavePreview.applicants || []).map((a) => [
        a.email || '',
        (a.name || '').replace(/[\r\n,]/g, ' '),
        (a.identities || []).join('|'),
        a.utm_source || '',
        a.referral_code || '',
        a.created_at || '',
        (() => {
          const j = (a.identities || []).join(' ').toLowerCase();
          if (/trans|femme|woman/.test(j)) return 'trans_femme';
          if (/admir/.test(j)) return 'admirer';
          return 'other';
        })(),
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nymphe-wave-${waveType}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── Edit handlers ────────────────────────────────────────────
  const openEdit = (a) => {
    setEditing(a);
    setEditForm({
      email: a.email || '',
      name: a.name || '',
      status: a.status || 'pending',
      identities: (a.identities || []).join(', '),
      notes: a.notes || '',
      utm_source: a.utm_source || '',
      referral_code: a.referral_code || '',
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm({});
  };

  const handleEditSave = async (e) => {
    if (e) e.preventDefault();
    if (!editing) return;
    setEditWorking(true);
    try {
      const payload = {
        email: editForm.email,
        name: editForm.name,
        status: editForm.status,
        identities: (editForm.identities || '')
          .split(',').map((s) => s.trim()).filter(Boolean),
        notes: editForm.notes,
        utm_source: editForm.utm_source,
        referral_code: editForm.referral_code,
      };
      await axios.patch(
        `${API}/api/admin/waitlist/${editing.id}`,
        payload,
        { headers: authHeaders },
      );
      toast.success('Saved.');
      closeEdit();
      fetchApplicants();
      fetchCounts();
      fetchBalance();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Save failed';
      toast.error(detail);
    }
    setEditWorking(false);
  };

  const handleRelease = async (id) => {
    setReleasing((m) => ({ ...m, [id]: true }));
    try {
      const { data } = await axios.post(
        `${API}/api/waitlist/${id}/approve`,
        {},
        { headers: authHeaders },
      );
      toast.success(`Released: ${data.email}`);
      fetchApplicants();
      fetchCounts();
      fetchBalance();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(detail || 'Already released');
      } else if (err?.response?.status === 502) {
        toast.error(detail || 'Email send failed — kept pending.');
      } else {
        toast.error(detail || 'Release failed');
      }
    } finally {
      setReleasing((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/api/waitlist/${id}/reject`, {}, { headers: authHeaders });
      toast.success('Rejected');
      setApplicants((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'rejected' } : a)));
    } catch {
      toast.error('Reject failed');
    }
  };

  const handleBatchRelease = async () => {
    if (batchRunning) return;
    const n = Math.max(1, Math.min(100, parseInt(batchCount, 10) || 10));
    setBatchRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/waitlist/release-batch?count=${n}`,
        {},
        { headers: authHeaders },
      );
      // Top-level message is composed server-side and tells the truth
      // about partial success / first failure reason.
      if (data.released_count > 0 && data.failed_count === 0) {
        toast.success(data.message);
      } else if (data.failed_count > 0) {
        toast.error(data.message);
      } else {
        toast.message(data.message);
      }
      fetchApplicants();
      fetchCounts();
      fetchBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Batch release failed');
    }
    setBatchRunning(false);
  };

  // ─── CSV Import handlers ─────────────────────────────────────
  const handleCsvFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setCsvFile(f);
    setCsvPreview(null);      // invalidate previous preview when file changes
    setCsvImported(null);
  };

  const postCsv = async (dryRun) => {
    if (!csvFile) return null;
    const form = new FormData();
    form.append('file', csvFile);
    const { data } = await axios.post(
      `${API}/api/waitlist/import-csv?dry_run=${dryRun ? 'true' : 'false'}`,
      form,
      { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  };

  const handleCsvPreview = async () => {
    if (csvWorking || !csvFile) return;
    setCsvWorking(true);
    try {
      const data = await postCsv(true);
      setCsvPreview(data);
      setCsvImported(null);
      toast.success(`Preview: ${data.would_import} will import, ${data.total_rows - data.would_import} skipped.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    }
    setCsvWorking(false);
  };

  const handleCsvImport = async () => {
    if (csvWorking || !csvFile || !csvPreview) return;
    setCsvWorking(true);
    try {
      const data = await postCsv(false);
      setCsvImported(data);
      toast.success(`Imported ${data.imported}.`);
      fetchApplicants();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    }
    setCsvWorking(false);
  };

  // ─── Change-password handler ─────────────────────────────────
  const resetPwForm = () => {
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwWorking) return;
    if (!pwCurrent || !pwNew || !pwConfirm) {
      toast.error('All fields are required.');
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error('New passwords do not match.');
      return;
    }
    if (pwNew.length < 12) {
      toast.error('New password must be at least 12 characters.');
      return;
    }
    if (pwNew === pwCurrent) {
      toast.error('New password must be different from current.');
      return;
    }
    setPwWorking(true);
    try {
      await axios.post(
        `${API}/api/admin/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        { headers: authHeaders },
      );
      // Session uses the new pw going forward; existing header was pre-rotation
      sessionStorage.setItem(PW_STORAGE_KEY, pwNew);
      setAuthPw(pwNew);
      resetPwForm();
      setPwOpen(false);
      toast.success('Password updated.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Password update failed.');
    }
    setPwWorking(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (!authPw) {
    return <AdminGate onAuthed={setAuthPw} />;
  }

  return (
    <div className="adm" data-testid="admin-page">
      <div className="adm-header">
        <h1 className="adm-title">Applications</h1>
        <div className="adm-search-wrap">
          <input
            className="adm-search"
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search"
          />
        </div>
      </div>

      {/* ── Access Funnel (read-only conversion panel) ── */}
      <section className="adm-funnel" data-testid="admin-funnel" aria-label="Access Funnel">
        <div className="adm-funnel-head">
          <h2 className="adm-funnel-title">Access Funnel</h2>
          <button
            type="button"
            className="adm-funnel-refresh"
            onClick={fetchFunnel}
            disabled={funnelLoading}
            data-testid="admin-funnel-refresh"
            aria-label="Refresh funnel"
          >
            {funnelLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {funnel ? (
          <>
            <div className="adm-funnel-grid">
              <div className="adm-funnel-cell" data-testid="funnel-form-started">
                <p className="adm-funnel-label">Form Started</p>
                <p className="adm-funnel-count">{funnel.form_started}</p>
                <p className="adm-funnel-meta">total</p>
              </div>
              <div className="adm-funnel-cell" data-testid="funnel-step-1">
                <p className="adm-funnel-label">Step 1 Completed</p>
                <p className="adm-funnel-count">{funnel.step_1_completed}</p>
                <p className="adm-funnel-meta">
                  {funnel.step_1_conversion !== null
                    ? `${funnel.step_1_conversion}% of Form Started`
                    : '— no data yet'}
                </p>
              </div>
              <div className="adm-funnel-cell" data-testid="funnel-submitted">
                <p className="adm-funnel-label">Submitted</p>
                <p className="adm-funnel-count">{funnel.submitted}</p>
                <p className="adm-funnel-meta">
                  {funnel.step_2_conversion !== null
                    ? `${funnel.step_2_conversion}% of Step 1`
                    : '— no data yet'}
                </p>
              </div>
              <div className="adm-funnel-cell" data-testid="funnel-partial-leads">
                <p className="adm-funnel-label">Partial Leads</p>
                <p className="adm-funnel-count">{funnel.partial_leads ?? 0}</p>
                <p className="adm-funnel-meta">captured before Step 2</p>
              </div>
            </div>
            <div className="adm-funnel-dropoff" data-testid="funnel-dropoff">
              <span>
                Step 1 drop-off:{' '}
                <strong data-testid="funnel-dropoff-step-1">
                  {funnel.step_1_dropoff !== null ? `${funnel.step_1_dropoff}%` : '—'}
                </strong>
              </span>
              <span className="adm-funnel-dropoff-sep" aria-hidden="true">·</span>
              <span>
                Step 2 drop-off:{' '}
                <strong data-testid="funnel-dropoff-step-2">
                  {funnel.step_2_dropoff !== null ? `${funnel.step_2_dropoff}%` : '—'}
                </strong>
              </span>
            </div>
          </>
        ) : (
          <p className="adm-funnel-empty" data-testid="admin-funnel-empty">
            {funnelLoading ? 'Loading funnel…' : 'No funnel data yet.'}
          </p>
        )}
      </section>

      {/* ── Promo Code Performance (read-only) ── */}
      <section
        className="adm-funnel adm-promo"
        data-testid="admin-promo"
        aria-label="Promo Code Performance"
      >
        <div className="adm-funnel-head">
          <h2 className="adm-funnel-title">Promo Code Performance</h2>
          <div className="adm-promo-range" role="group" aria-label="Timeframe">
            {[7, 30].map((d) => (
              <button
                key={d}
                type="button"
                className={`adm-promo-range-btn${promoDays === d ? ' is-active' : ''}`}
                onClick={() => setPromoDays(d)}
                disabled={promoLoading && promoDays !== d}
                data-testid={`admin-promo-range-${d}`}
                aria-pressed={promoDays === d}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>

        {promo && promo.top && promo.top.length > 0 ? (
          <ol className="adm-promo-list" data-testid="admin-promo-list">
            {promo.top.map((row, i) => (
              <li
                key={row.code}
                className="adm-promo-row"
                data-testid={`admin-promo-row-${i + 1}`}
              >
                <span className="adm-promo-rank" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="adm-promo-code" data-testid={`admin-promo-code-${i + 1}`}>
                  {row.code}
                </span>
                <span className="adm-promo-count" data-testid={`admin-promo-count-${i + 1}`}>
                  {row.count}
                  <span className="adm-promo-count-meta"> signup{row.count === 1 ? '' : 's'}</span>
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="adm-funnel-empty" data-testid="admin-promo-empty">
            {promoLoading ? 'Loading…' : 'No promo codes submitted yet.'}
          </p>
        )}
      </section>

      {/* ── Launch Balance Tracker ───────────────────────────────── */}
      <section
        className="adm-funnel adm-launch"
        data-testid="admin-launch-balance"
        aria-label="Launch Balance Tracker"
      >
        <div className="adm-funnel-head">
          <h2 className="adm-funnel-title">Launch Balance Tracker</h2>
          <button
            type="button"
            className="adm-funnel-refresh"
            onClick={fetchBalance}
            disabled={balanceLoading}
            data-testid="admin-launch-refresh"
          >
            {balanceLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {balance ? (() => {
          const goal = balance.goal?.total || 250;
          const real = balance.real_pending || 0;
          const pct = Math.min(100, Math.round((real / goal) * 100));
          const softPct = Math.min(100, Math.round(((balance.goal?.soft_minimum || 150) / goal) * 100));
          return (
            <>
              <div className="adm-launch-top">
                <div className="adm-launch-stat">
                  <p className="adm-funnel-label">Real pending</p>
                  <p className="adm-funnel-count" data-testid="launch-real-pending">{real}</p>
                  <p className="adm-funnel-meta">
                    {balance.goal?.remaining ?? 0} to go · soft min {balance.goal?.soft_minimum ?? 150}
                  </p>
                </div>
                <div className="adm-launch-progress">
                  <div className="adm-launch-bar" aria-hidden="true">
                    <div
                      className="adm-launch-bar-soft"
                      style={{ left: `${softPct}%` }}
                      title="Soft launch minimum (150)"
                    />
                    <div className="adm-launch-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="adm-funnel-meta">
                    {pct}% of {goal} goal · first wave {balance.first_wave?.size ?? 75} ·
                    {' '}ongoing {balance.ongoing_wave?.size ?? 25}
                  </p>
                </div>
              </div>
              <div className="adm-launch-cats" data-testid="launch-categories">
                {[
                  { key: 'trans_femme', label: 'Trans women + femmes' },
                  { key: 'admirer',     label: 'Admirers' },
                  { key: 'other',       label: 'Other / unknown' },
                ].map(({ key, label }) => {
                  const have = balance.by_category?.[key] || 0;
                  const target = balance.goal?.by_category?.[key] || 0;
                  const cpct = target ? Math.min(100, Math.round((have / target) * 100)) : 0;
                  const remaining = Math.max(target - have, 0);
                  return (
                    <div key={key} className="adm-launch-cat" data-testid={`launch-cat-${key}`}>
                      <div className="adm-launch-cat-head">
                        <span className="adm-launch-cat-label">{label}</span>
                        <span className="adm-launch-cat-stat">{have} / {target}</span>
                      </div>
                      <div className="adm-launch-bar adm-launch-bar--mini" aria-hidden="true">
                        <div className="adm-launch-bar-fill" style={{ width: `${cpct}%` }} />
                      </div>
                      <p className="adm-funnel-meta">
                        {remaining > 0 ? `${remaining} more to hit goal` : 'Target met'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })() : (
          <p className="adm-funnel-empty">{balanceLoading ? 'Loading…' : 'No data.'}</p>
        )}
      </section>

      {/* ── Wave Release Control ─────────────────────────────────── */}
      <section
        className="adm-funnel adm-wave"
        data-testid="admin-wave-control"
        aria-label="Wave Release Control"
      >
        <div className="adm-funnel-head">
          <h2 className="adm-funnel-title">Wave Release Control</h2>
          <span className="adm-wave-mode" data-testid="admin-wave-mode">
            AI-Suggested · Auto Release Mode: <strong>OFF</strong>
          </span>
        </div>
        <div className="adm-wave-controls">
          <div className="adm-wave-type" role="group" aria-label="Wave type">
            {[
              { k: 'first',   l: `First wave (${balance?.first_wave?.size || 75})` },
              { k: 'ongoing', l: `Ongoing wave (${balance?.ongoing_wave?.size || 25})` },
            ].map(({ k, l }) => (
              <button
                key={k}
                type="button"
                className={`adm-promo-range-btn${waveType === k ? ' is-active' : ''}`}
                onClick={() => { setWaveType(k); setWavePreview(null); }}
                data-testid={`admin-wave-type-${k}`}
                aria-pressed={waveType === k}
              >
                {l}
              </button>
            ))}
          </div>
          <label className="adm-wave-num">
            <span>Wave #</span>
            <input
              type="number"
              min="1"
              max="999"
              value={waveNumber}
              onChange={(e) => setWaveNumber(e.target.value)}
              className="adm-wave-num-input"
              data-testid="admin-wave-number"
            />
          </label>
          <button
            type="button"
            className="adm-act adm-act--approve"
            onClick={handleGenerateWave}
            disabled={waveWorking}
            data-testid="admin-wave-generate"
          >
            {waveWorking && !wavePreview ? 'Generating…' : 'Generate Suggested Wave'}
          </button>
          {wavePreview && (
            <>
              <button
                type="button"
                className="adm-act adm-act--reject"
                onClick={() => { setWavePreview(null); setWaveSelection({}); }}
                data-testid="admin-wave-skip"
              >
                Skip / Regenerate
              </button>
              <button
                type="button"
                className="adm-act"
                onClick={handleExportWaveCsv}
                data-testid="admin-wave-export"
              >
                Export CSV
              </button>
              <button
                type="button"
                className="adm-act adm-act--approve"
                onClick={handleReleaseWave}
                disabled={waveWorking}
                data-testid="admin-wave-release"
              >
                {waveWorking ? 'Releasing…' : `Release This Wave (${
                  Object.values(waveSelection).filter(Boolean).length
                })`}
              </button>
            </>
          )}
        </div>

        {wavePreview && (
          <div className="adm-wave-preview" data-testid="admin-wave-preview">
            <div className="adm-wave-fill">
              {Object.entries(wavePreview.fill_report || {}).map(([cat, info]) => (
                <span key={cat} className="adm-wave-fill-cell" data-testid={`wave-fill-${cat}`}>
                  <strong>{cat.replace('_', ' ')}</strong>{' '}
                  {info.picked}/{info.target}
                  {info.short_by > 0 && (
                    <span className="adm-wave-fill-short"> · short {info.short_by}</span>
                  )}
                </span>
              ))}
            </div>
            <ul className="adm-wave-list">
              {(wavePreview.applicants || []).map((a) => {
                const cat = (() => {
                  const j = (a.identities || []).join(' ').toLowerCase();
                  if (/trans|femme|woman/.test(j)) return 'trans_femme';
                  if (/admir/.test(j)) return 'admirer';
                  return 'other';
                })();
                return (
                  <li key={a.id} className="adm-wave-row" data-testid={`wave-row-${a.id}`}>
                    <input
                      type="checkbox"
                      checked={!!waveSelection[a.id]}
                      onChange={(e) => setWaveSelection((m) => ({ ...m, [a.id]: e.target.checked }))}
                      data-testid={`wave-check-${a.id}`}
                    />
                    <span className="adm-wave-email">{a.email}</span>
                    <span className="adm-wave-cat" data-testid={`wave-cat-${a.id}`}>{cat.replace('_', ' ')}</span>
                    <span className="adm-wave-date">{formatDate(a.created_at)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      <div className="adm-filters" data-testid="admin-filters">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.key}
            className={`adm-filter ${filter === s.key ? 'adm-filter--active' : ''}`}
            onClick={() => setFilter(s.key)}
            data-testid={`filter-${s.key}`}
          >
            {s.label}
            {counts && counts[s.key] !== undefined && (
              <span className="adm-filter-count" data-testid={`filter-count-${s.key}`}>
                {counts[s.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── CSV Import (collapsed by default) ── */}
      <div className="adm-csv" data-testid="admin-csv">
        <button
          type="button"
          className="adm-csv-toggle"
          onClick={() => setCsvOpen((v) => !v)}
          data-testid="admin-csv-toggle"
        >
          {csvOpen ? '−' : '+'} Import CSV
        </button>
        {csvOpen && (
          <div className="adm-csv-body">
            <p className="adm-csv-hint">
              Columns: <code>email</code> (required), <code>status</code>, <code>cohort</code>,
              {' '}<code>name</code>, <code>notes</code>, <code>source</code>, <code>created_at</code>.
              {' '}Preview is required before importing. No invite emails are sent.
            </p>
            <div className="adm-csv-row">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsvFileChange}
                data-testid="admin-csv-file"
                className="adm-csv-file"
              />
              <button
                type="button"
                className="adm-csv-btn adm-csv-btn--ghost"
                disabled={!csvFile || csvWorking}
                onClick={handleCsvPreview}
                data-testid="admin-csv-preview-btn"
              >
                {csvWorking && !csvImported ? '...' : 'Preview'}
              </button>
              <button
                type="button"
                className="adm-csv-btn"
                disabled={!csvFile || !csvPreview || csvWorking}
                onClick={handleCsvImport}
                data-testid="admin-csv-import-btn"
              >
                {csvWorking && csvPreview ? '...' : 'Import'}
              </button>
            </div>

            {(csvPreview || csvImported) && (
              <div className="adm-csv-report" data-testid="admin-csv-report">
                {(() => {
                  const r = csvImported || csvPreview;
                  const label = csvImported ? 'Imported' : 'Preview';
                  return (
                    <>
                      <p className="adm-csv-report-head">
                        {label}: <strong>{csvImported ? r.imported : r.would_import}</strong> of {r.total_rows} rows
                      </p>
                      <ul className="adm-csv-stats">
                        <li>duplicates in db: {r.skipped_duplicate_in_db}</li>
                        <li>duplicates in file: {r.skipped_duplicate_in_file}</li>
                        <li>invalid email: {r.rejected_invalid_email}</li>
                        <li>invalid status: {r.rejected_invalid_status}</li>
                      </ul>
                      {r.errors?.length > 0 && (
                        <details className="adm-csv-errors">
                          <summary>{r.errors.length} issue{r.errors.length === 1 ? '' : 's'}</summary>
                          <ul>
                            {r.errors.map((e, i) => (
                              <li key={i}>
                                {e.row ? `row ${e.row} · ` : ''}
                                {e.email} — {e.reason}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {filter === 'pending' && (
        <div className="adm-batch" data-testid="admin-batch-bar">
          <label className="adm-batch-label">Release next</label>
          <input
            type="number"
            min={1}
            max={100}
            className="adm-batch-input"
            value={batchCount}
            onChange={(e) => setBatchCount(e.target.value)}
            data-testid="admin-batch-count"
          />
          <button
            className="adm-batch-btn"
            onClick={handleBatchRelease}
            disabled={batchRunning}
            data-testid="admin-batch-release"
          >
            {batchRunning ? 'Releasing...' : `Release next ${batchCount}`}
          </button>
        </div>
      )}

      {loading ? (
        <div className="adm-empty">Loading...</div>
      ) : applicants.length === 0 ? (
        <div className="adm-empty" data-testid="admin-empty">
          No {filter === 'all' ? '' : filter} applications.
        </div>
      ) : (
        <div className="adm-list" data-testid="admin-list">
          {applicants.map((a) => (
            <div key={a.id} className="adm-card" data-testid={`card-${a.id}`}>
              <div className="adm-card-top">
                <div className="adm-card-main">
                  <span className="adm-card-email">{a.email}</span>
                  <span className="adm-card-date">{formatDate(a.created_at)}</span>
                </div>
                <span className={`adm-badge adm-badge--${a.status || 'pending'}`}>
                  {a.status || 'pending'}
                </span>
              </div>

              {(a.identities?.length > 0 || a.reason || a.invite_token || a.cohort || a.source || a.utm_source || a.referral_code || a.notes || a.quality_flag) && (
                <div className="adm-card-details">
                  {(a.cohort || a.source || a.utm_source || a.referral_code) && (
                    <span className="adm-card-meta" data-testid={`card-meta-${a.id}`}>
                      {[a.cohort, a.source, a.utm_source, a.referral_code].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {a.identities?.length > 0 && (
                    <div className="adm-card-ids">
                      {a.identities.map((id) => (
                        <span key={id} className="adm-id-pill">
                          {id}
                        </span>
                      ))}
                    </div>
                  )}
                  {a.notes && <p className="adm-card-reason">{a.notes}</p>}
                  {a.reason && <p className="adm-card-reason">{a.reason}</p>}
                  {a.quality_flag && (
                    <span className="adm-card-flag" data-testid={`card-flag-${a.id}`}>
                      Quality flag: {a.quality_flag}
                    </span>
                  )}
                  {a.wave_number && (
                    <span className="adm-card-meta">
                      Wave {a.wave_number}{a.released_at ? ` · ${formatDate(a.released_at)}` : ''}
                    </span>
                  )}
                  {a.invite_token && (
                    <span className="adm-card-invite">
                      Invite: <span className="adm-invite-status">{a.invite_status || 'active'}</span>
                    </span>
                  )}
                </div>
              )}

              <div className="adm-card-actions">
                {(a.status === 'pending' || a.status === 'partial' || !a.status) && (
                  <button
                    className="adm-act adm-act--approve"
                    onClick={() => handleRelease(a.id)}
                    disabled={!!releasing[a.id] || a.status === 'partial'}
                    data-testid={`release-${a.id}`}
                    title={a.status === 'partial' ? 'Partial leads cannot be released until Step 2.' : 'Release'}
                  >
                    {releasing[a.id] ? '...' : 'Release'}
                  </button>
                )}
                {(a.status === 'pending' || a.status === 'partial' || !a.status) && (
                  <button
                    className="adm-act adm-act--reject"
                    onClick={() => handleReject(a.id)}
                    data-testid={`reject-${a.id}`}
                  >
                    Reject
                  </button>
                )}
                <button
                  className="adm-act adm-act--edit"
                  onClick={() => openEdit(a)}
                  data-testid={`edit-${a.id}`}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Change admin password (internal utility, collapsed by default) ── */}
      <div className="adm-pw" data-testid="admin-pw">
        <button
          type="button"
          className="adm-pw-toggle"
          onClick={() => { setPwOpen((v) => !v); if (pwOpen) resetPwForm(); }}
          data-testid="admin-pw-toggle"
        >
          {pwOpen ? '−' : '+'} Change admin password
        </button>
        {pwOpen && (
          <form className="adm-pw-body" onSubmit={handleChangePassword} autoComplete="off">
            <input
              type="password"
              className="adm-pw-input"
              placeholder="Current password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="admin-pw-current"
            />
            <input
              type="password"
              className="adm-pw-input"
              placeholder="New password (min 12 characters)"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              autoComplete="new-password"
              data-testid="admin-pw-new"
            />
            <input
              type="password"
              className="adm-pw-input"
              placeholder="Confirm new password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="admin-pw-confirm"
            />
            <button
              type="submit"
              className="adm-pw-submit"
              disabled={pwWorking}
              data-testid="admin-pw-submit"
            >
              {pwWorking ? '...' : 'Update password'}
            </button>
          </form>
        )}
      </div>

      {/* ── Edit modal ───────────────────────────────────────────── */}
      {editing && (
        <div className="adm-modal" role="dialog" aria-modal="true" data-testid="admin-edit-modal">
          <div className="adm-modal-backdrop" onClick={closeEdit} />
          <form className="adm-modal-card" onSubmit={handleEditSave}>
            <div className="adm-modal-head">
              <h3 className="adm-modal-title">Edit record</h3>
              <button
                type="button"
                className="adm-modal-x"
                onClick={closeEdit}
                aria-label="Close"
                data-testid="admin-edit-close"
              >×</button>
            </div>
            <p className="adm-modal-hint">No emails are sent on edit.</p>
            <label className="adm-modal-field">
              <span>Email</span>
              <input
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="admin-edit-email"
              />
            </label>
            <label className="adm-modal-field">
              <span>Name</span>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="admin-edit-name"
              />
            </label>
            <label className="adm-modal-field">
              <span>Status</span>
              <select
                value={editForm.status || 'pending'}
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                data-testid="admin-edit-status"
              >
                {['partial', 'pending', 'approved', 'used', 'rejected', 'flagged', 'archived_test', 'excluded'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="adm-modal-field">
              <span>Identities (comma-separated)</span>
              <input
                type="text"
                value={editForm.identities || ''}
                onChange={(e) => setEditForm((f) => ({ ...f, identities: e.target.value }))}
                placeholder="e.g. trans, femme, admirer"
                data-testid="admin-edit-identities"
              />
            </label>
            <label className="adm-modal-field">
              <span>Source / UTM</span>
              <input
                type="text"
                value={editForm.utm_source || ''}
                onChange={(e) => setEditForm((f) => ({ ...f, utm_source: e.target.value }))}
                data-testid="admin-edit-source"
              />
            </label>
            <label className="adm-modal-field">
              <span>Promo / Referral code</span>
              <input
                type="text"
                value={editForm.referral_code || ''}
                onChange={(e) => setEditForm((f) => ({ ...f, referral_code: e.target.value }))}
                data-testid="admin-edit-promo"
              />
            </label>
            <label className="adm-modal-field">
              <span>Notes</span>
              <textarea
                rows={3}
                value={editForm.notes || ''}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                data-testid="admin-edit-notes"
              />
            </label>
            <div className="adm-modal-actions">
              <button
                type="button"
                className="adm-act adm-act--reject"
                onClick={closeEdit}
                data-testid="admin-edit-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="adm-act adm-act--approve"
                disabled={editWorking}
                data-testid="admin-edit-save"
              >
                {editWorking ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
